import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, MapPinOff } from "lucide-react";

/** Driver "Share my location" toggle — uses navigator.geolocation.watchPosition.
 *  Pushes to backend every ~15s while the trip is active.
 */
export default function ShareLocationButton({ booking }) {
  const [sharing, setSharing] = useState(false);
  const [lastEta, setLastEta] = useState(null);
  const watchIdRef = useRef(null);
  const lastSentRef = useRef(0);

  useEffect(() => () => stop(), []);

  const start = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported by this browser");
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        const now = Date.now();
        if (now - lastSentRef.current < 12000) return; // throttle to ~15s
        lastSentRef.current = now;
        try {
          const r = await api.post("/drivers/me/location", {
            booking_id: booking.booking_id,
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            heading: pos.coords.heading,
            speed_mph: pos.coords.speed ? pos.coords.speed * 2.23694 : null,
          });
          if (r.data.eta_minutes != null) setLastEta(r.data.eta_minutes);
          if (r.data.auto_status) toast.success(`Auto: ${r.data.auto_status.replace("_", " ")}`);
        } catch (e) {
          // silent
        }
      },
      (err) => {
        toast.error(`GPS error: ${err.message}`);
        stop();
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    setSharing(true);
    toast.success("Sharing location with dispatch");
  };

  const stop = () => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setSharing(false);
  };

  return (
    <div className="border border-white/10 bg-[#0F0F0F] p-4 space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Live location</div>
          <div className="text-sm text-[#A1A1AA]">{sharing ? "Sharing with dispatch & customer" : "Off"}</div>
        </div>
        <button
          data-testid={sharing ? "stop-sharing-gps" : "start-sharing-gps"}
          onClick={sharing ? stop : start}
          className={sharing ? "btn-ghost !py-2 !px-4 text-[11px] text-red-400 border-red-400/30" : "btn-gold !py-2 !px-4 text-[11px]"}
        >
          {sharing ? <><MapPinOff size={12}/>Stop</> : <><MapPin size={12}/>Start sharing</>}
        </button>
      </div>
      {sharing && lastEta != null && (
        <div className="text-xs text-[#D4AF37]">ETA to pickup: <span className="font-serif text-base">{lastEta}</span> min</div>
      )}
    </div>
  );
}
